import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { useColors } from '../../theme/ColorModeContext';
import { getTechQuizResult, TechQuizResult } from '../../api/Api';
import { DIFFICULTY_ORDER, QUIZ_DURATION_MS } from './quizEngine';
import { getTechQuiz } from './registry';
import ResultSummary, { TierScores } from './ResultSummary';
import ShareResult from './ShareResult';
import { shareText } from './share';

/** Public page behind a quiz share link; the API's share page redirects browsers here. */
const QuizResult = () => {
    const colors = useColors();
    const { shareId } = useParams();
    // `undefined` while loading, `null` when the result does not exist.
    const [result, setResult] = useState<TechQuizResult | null | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        setResult(undefined);
        if (!shareId) {
            setResult(null);
            return;
        }
        getTechQuizResult(shareId).then((data) => {
            if (!cancelled) setResult(data);
        });
        return () => {
            cancelled = true;
        };
    }, [shareId]);

    if (result === undefined) {
        return (
            <PageContainer maxWidth="md">
                <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (result === null) {
        return (
            <PageContainer maxWidth="md">
                <CyberpunkTitle title="Result not found" mb="24px" />
                <GlowCard glowOnHover={false}>
                    <Box sx={{ p: { xs: '24px', md: '36px' } }}>
                        <Typography sx={{ color: colors.text.secondary, mb: 3 }}>
                            This quiz result doesn&apos;t exist. Check that the link was copied in full.
                        </Typography>
                        <Button component={RouterLink} to="/" variant="contained">
                            Go to WEB3PWN
                        </Button>
                    </Box>
                </GlowCard>
            </PageContainer>
        );
    }

    const quiz = getTechQuiz(result.quiz_slug);
    const title = quiz?.title ?? result.quiz_title;
    const correct = result.score ?? 0;
    const tierTotal = Math.round(result.total_questions / DIFFICULTY_ORDER.length);
    const perTier: TierScores = {
        easy: { correct: result.score_easy ?? 0, total: tierTotal },
        medium: { correct: result.score_medium ?? 0, total: tierTotal },
        hard: { correct: result.score_hard ?? 0, total: tierTotal },
    };
    const completedAt = result.finished_at
        ? new Date(result.finished_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        : null;

    return (
        <PageContainer maxWidth="md">
            <CyberpunkTitle title="Quiz Result" subtitle={title} mb="24px" />

            <GlowCard glowOnHover={false} gradientBorder>
                <Box sx={{ p: { xs: '28px', md: '40px' }, textAlign: 'center' }}>
                    {result.username && (
                        <Typography
                            sx={{
                                fontFamily: '"Share Tech Mono", monospace',
                                fontSize: '0.85rem',
                                color: colors.text.secondary,
                                mb: 2.5,
                            }}
                        >
                            Result by{' '}
                            <Link
                                component={RouterLink}
                                to={`/profile/${encodeURIComponent(result.username)}`}
                                sx={{ color: colors.accent.cyan, fontWeight: 700 }}
                            >
                                {result.username}
                            </Link>
                        </Typography>
                    )}

                    <ResultSummary
                        correct={correct}
                        total={result.total_questions}
                        perTier={perTier}
                        elapsedMs={Math.min((result.duration_seconds ?? 0) * 1000, QUIZ_DURATION_MS)}
                        timedOut={result.timed_out}
                    />

                    {completedAt && (
                        <Typography sx={{ color: colors.text.muted, fontSize: '0.82rem', mt: 1.5 }}>
                            Completed {completedAt}
                        </Typography>
                    )}

                    <ShareResult shareUrl={result.share_url} text={shareText(correct, result.total_questions, title)} />

                    {quiz && (
                        <Button
                            component={RouterLink}
                            to={quiz.path}
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{ mt: 3, fontFamily: '"Orbitron", sans-serif', fontWeight: 700 }}
                        >
                            Take the {title}
                        </Button>
                    )}
                </Box>
            </GlowCard>
        </PageContainer>
    );
};

export default QuizResult;
